import {
  signOut,
  useSessionContext,
} from 'supertokens-auth-react/recipe/session';
import { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerTrigger,
} from '@/components/shadcn/drawer';
import { HamburgerMenuIcon } from '@radix-ui/react-icons';
import UserAvatar from '../header/user-avatar';
import {
  hasAdminRole,
  hasGrantManagerRole,
  hasSocialMediaManagerRole,
  useCurrentUser,
} from '@/lib/auth-service';
import { Separator } from '@radix-ui/react-dropdown-menu';
import { Button } from '@/components/shadcn/button';
import { Dialog } from '@/components/shadcn/dialog';
import { Settings } from '../settings';
import { OrganizationSelector } from '../header/organization-selector';
import { useOrganizationContext } from '@/contexts/organization-context';
import { env } from 'next-runtime-env';
import Link from 'next/link';
import {
  IS_HANDLE_CALLBACK_QUERY_PARAM,
  SOCIAL_PLATFORM_QUERY_PARAM,
} from '@/consts/social-media';
import { useRouter } from 'next/router';
import { logInfo } from '@/lib/rum';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { RoutePaths } from '@/consts/routes';

const WEBSITE_DOMAIN = env('WEBSITE');

export function MobileMenu() {
  const session = useSessionContext();
  const { user, isLoading } = useCurrentUser();
  const { organizationId, isGrantUser, isMaggieUser, organizationList } =
    useOrganizationContext();
  const isMaggieDashboardEnabled =
    useFeatureFlagEnabled('maggie-dashboard') === true;
  const isGrantDashboardEnabled =
    useFeatureFlagEnabled('grant-dashboard') === true;
  const isGrantDashboardVeeEnabled =
    useFeatureFlagEnabled('grant-dashboard-orgs') === true;
  const isWorkspaceEnabled = useFeatureFlagEnabled('workspace') === true;

  const [openDrawer, setOpenDrawer] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const router = useRouter();

  useEffect(() => {
    logInfo('Mobile user avatar menu', {
      query: router.query,
    });
    if (router.query) {
      const isHandleCallback = router.query[IS_HANDLE_CALLBACK_QUERY_PARAM];
      const socialMediaPlatform = router.query[SOCIAL_PLATFORM_QUERY_PARAM];
      const settingsTab = router.query['settingstab'];

      if ((isHandleCallback && socialMediaPlatform) || settingsTab) {
        setOpenDialog(true);
      }
    }
  }, [router.query]);

  if (isLoading || session.loading || !session.doesSessionExist) {
    return null;
  }

  return (
    <Dialog open={openDialog} onOpenChange={setOpenDialog}>
      <Drawer open={openDrawer} onOpenChange={setOpenDrawer}>
        <DrawerTrigger
          onClick={() => {
            setOpenDrawer(true);
          }}
        >
          <HamburgerMenuIcon className='h-8 w-8' />
        </DrawerTrigger>
        <DrawerContent className='flex h-4/5'>
          <div className='overflow-y-scroll'>
            <div className='flex items-center gap-4 px-4'>
              <UserAvatar
                username={user.primaryEmail}
                imgSrc=''
                className='h-12 w-12'
              />
              <span className='text-black/60'>{user.primaryEmail}</span>
            </div>
            <Separator className='my-2 h-[1px] bg-muted' />
            {organizationList && organizationList?.length > 1 && (
              <>
                <div className='flex flex-col gap-4 px-4'>
                  <label>Organization Select</label>
                  <OrganizationSelector onSelect={() => setOpenDrawer(false)} />
                </div>
                <Separator className='my-2 h-[1px] bg-muted' />
              </>
            )}

            <div className='flex flex-col px-4'>
              <label>Account Menu</label>
              <div className='flex flex-col items-center gap-4'>
                <div className=''>
                  <Button
                    variant={'ghost'}
                    className='text-neutral-900'
                    onClick={() => {
                      setOpenDrawer(false);
                      setOpenDialog(true);
                    }}
                  >
                    Settings
                  </Button>
                </div>
              </div>
            </div>
            {isGrantUser &&
              hasGrantManagerRole(session.accessTokenPayload['st-role']?.v) && (
                <>
                  <Separator className='my-2 h-[1px] bg-muted' />
                  <div className='flex flex-col px-4'>
                    <label>Grant Menu</label>
                    <div className='flex flex-col items-center gap-4'>
                      <Link
                        href={
                          isGrantDashboardEnabled || isGrantDashboardVeeEnabled
                            ? `/grant?organizationId=${organizationId}`
                            : `/grant/opportunities?organizationId=${organizationId}`
                        }
                        onClick={() => setOpenDrawer(false)}
                      >
                        <Button variant={'ghost'} className='text-neutral-900'>
                          Grant Opportunities
                        </Button>
                      </Link>
                      {hasAdminRole(
                        session.accessTokenPayload['st-role']?.v
                      ) && (
                        <Link
                          href={`/grant/grants?organizationId=${organizationId}`}
                          onClick={() => setOpenDrawer(false)}
                        >
                          <Button
                            className='text-neutral-900'
                            variant={'ghost'}
                          >
                            Grants DB Operations
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </>
              )}
            {isMaggieUser &&
              hasSocialMediaManagerRole(
                session.accessTokenPayload['st-role']?.v
              ) && (
                <>
                  <Separator className='my-2 h-[1px] bg-muted' />
                  <div className='flex flex-col px-4'>
                    <label>Maggie Menu</label>
                    <div className='flex flex-col items-center gap-4'>
                      {hasAdminRole(
                        session.accessTokenPayload['st-role']?.v
                      ) && (
                        <Link
                          href={`${WEBSITE_DOMAIN}/maggie/source-content-management/org/${organizationId}`}
                        >
                          <Button
                            className='text-neutral-900'
                            variant={'ghost'}
                          >
                            Source Content Management
                          </Button>
                        </Link>
                      )}
                      {hasAdminRole(
                        session.accessTokenPayload['st-role']?.v
                      ) && (
                        <Link
                          href={`${WEBSITE_DOMAIN}/maggie/posts-overview/org/${organizationId}`}
                        >
                          <Button
                            className='text-neutral-900'
                            variant={'ghost'}
                          >
                            Posts Overview
                          </Button>
                        </Link>
                      )}
                      {hasAdminRole(
                        session.accessTokenPayload['st-role']?.v
                      ) && (
                        <Link
                          href={`${WEBSITE_DOMAIN}/maggie/events-calendar/org/${organizationId}`}
                        >
                          <Button
                            className='text-neutral-900'
                            variant={'ghost'}
                          >
                            Organization Event Calendar
                          </Button>
                        </Link>
                      )}
                      {hasSocialMediaManagerRole(
                        session.accessTokenPayload['st-role']?.v
                      ) && (
                        <Link
                          href={
                            isMaggieDashboardEnabled
                              ? `/maggie?organizationId=${organizationId}`
                              : `${WEBSITE_DOMAIN}/maggie/posts/org/${organizationId}`
                          }
                        >
                          <Button
                            className='text-neutral-900'
                            variant={'ghost'}
                          >
                            Posts Approval
                          </Button>
                        </Link>
                      )}
                      {hasSocialMediaManagerRole(
                        session.accessTokenPayload['st-role']?.v
                      ) && (
                        <Link
                          href={`${WEBSITE_DOMAIN}/maggie/social-calendar-management/org/${organizationId}`}
                        >
                          <Button
                            className='text-neutral-900'
                            variant={'ghost'}
                          >
                            Social Calendar
                          </Button>
                        </Link>
                      )}

                      {hasAdminRole(
                        session.accessTokenPayload['st-role']?.v
                      ) && (
                        <Link
                          href={`${WEBSITE_DOMAIN}/maggie/writing-preferences?organizationId=${organizationId}`}
                        >
                          <Button
                            className='text-neutral-900'
                            variant={'ghost'}
                          >
                            Writing Preferences
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </>
              )}

            <>
              <Separator className='my-2 h-[1px] bg-muted' />
              <div className='flex flex-col px-4'>
                <label>Vee Menu</label>
                <div className='flex flex-col gap-4 text-center'>
                  <Link
                    href={`${WEBSITE_DOMAIN}/`}
                    onClick={() => setOpenDrawer(false)}
                  >
                    <Button className='text-neutral-900' variant={'ghost'}>
                      Home
                    </Button>
                  </Link>
                  {isWorkspaceEnabled && (
                    <Link
                      href={`${WEBSITE_DOMAIN}${RoutePaths.Workspace}?organizationId=${organizationId}`}
                      onClick={() => setOpenDrawer(false)}
                    >
                      <Button className='text-neutral-900' variant={'ghost'}>
                        Workspace
                      </Button>
                    </Link>
                  )}
                  {hasAdminRole(session.accessTokenPayload['st-role']?.v) && (
                    <Link
                      href={`${WEBSITE_DOMAIN}/maggie/settings`}
                      onClick={() => setOpenDrawer(false)}
                    >
                      <Button className='text-neutral-900' variant={'ghost'}>
                        Settings
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </>
          </div>
          <DrawerFooter>
            <Button variant='destructive' onClick={async () => await signOut()}>
              Logout
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <Settings />
    </Dialog>
  );
}
